import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo, ThemeToggle } from "@/components/AppShell";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex items-center justify-between p-4">
        <Link to="/"><Logo /></Link>
        <ThemeToggle />
      </div>
      <div className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm rounded-2xl border bg-card p-6" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <MailCheck className="mx-auto h-8 w-8 text-primary" />
              <h1 className="mt-3 font-display text-xl font-semibold">Check your email</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                If an account exists for <span className="font-medium text-foreground">{email}</span>, a reset link is on its way.
              </p>
              <Button asChild className="mt-6 w-full" data-testid="forgot-password-back-to-login">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-xl font-semibold">Reset your password</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Enter your email and we'll send you a link to choose a new password.
              </p>
              <form onSubmit={submit} className="mt-5 space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input id="forgot-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" data-testid="forgot-password-email-input" />
                </div>
                <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-password-submit-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send reset link
                </Button>
              </form>
              <p className="mt-4 text-center text-sm text-muted-foreground">
                <Link to="/login" className="font-medium text-primary hover:underline" data-testid="forgot-password-login-link">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
